<template>
  <div class="container-fluid content-pad content-pad--big-top">
    <div class="content-wrap-x-small">
      <dx-progressbar :steps="4" :active-step="4" />
    </div>
    <div class="row mt-5">
      <div class="col-12 col-md-8">
        <h3>{{ $t('offer.summary.headline') }}</h3>

        <!-- DOWNLOADS ------------------------------------------------------------>
        <dx-download-list
          :items="downloads"
          external-url
          :headline="`${$t('offer.summary.headDocs')} ${$t(
            `partnerPortal.tariff.${activeTariff}.name`,
          )}`"
          class="my-5"
          tag-type="h5"
          target="_blank"
        />

        <!-- BASIC QUESTIONS ------------------------------------------------------------>
        <dx-edit-data-toggle
          ref="basicQuestionsToogle"
          :headline="$t('offer.summary.insuranceQuestions')"
          class="content-border-bottom pb-5"
          :callback="saveBasicQuestionsForm"
          :show-buttons="false"
          :class="{ 'has-errors': !validForms.basicQuestions }"
        >
          <template slot="data-dynamic" slot-scope="{ editMode }">
            <dx-private-basic-questions
              v-if="tariffType === 'private'"
              ref="privateIntroForm"
              :editable="editMode"
              @change="debouncedCalcPremia"
            />
            <dx-business-basic-questions
              v-else-if="tariffType === 'business'"
              ref="businessIntroForm"
              :editable="editMode"
              @change="debouncedCalcPremia"
            />
            <dx-business-move-basic-questions
              v-else-if="tariffType === 'b-move'"
              ref="businessMoveIntroForm"
              :editable="editMode"
              @change="debouncedCalcPremia"
            />
            <a v-else :href="tariffURL" class="secondary-link">
              {{ $t('offer.chooseProduct') }}
            </a>
          </template>
        </dx-edit-data-toggle>

        <!-- POLICYHOLDER DATA ------------------------------------------------------------>
        <dx-edit-data-toggle
          ref="policyHolderDataToogle"
          :headline="$t('offer.summary.infoPolicyholder')"
          class="content-border-bottom pb-5 mt-5"
          :callback="() => $refs[formRefPolicyHolder].saveForm()"
          :show-buttons="false"
          :class="{ 'has-errors': !validForms.policyHolderData }"
        >
          <template slot="data-dynamic" slot-scope="{ editMode }">
            <dx-policy-holder-data-form
              v-if="tariffType === 'private'"
              ref="policyHolderDataForm"
              :editable="editMode"
            />

            <dx-business-policy-holder-data-form
              v-else
              ref="businessPolicyHolderDataForm"
              :editable="editMode"
            />
          </template>
        </dx-edit-data-toggle>

        <!-- ASSESSMENT QUESTIONS ------------------------------------------------------------>
        <dx-edit-data-toggle
          ref="answeredQuestionsToogle"
          :headline="$t('offer.summary.answeredQuestions')"
          class="content-border-bottom pb-5 mt-5"
          tag-type="h4"
          :callback="() => $refs.assessmentQuestionsForm.saveForm()"
          :show-buttons="false"
          :class="{ 'has-errors': !validForms.answeredQuestions }"
        >
          <template slot="data-dynamic" slot-scope="{ editMode }">
            <dx-assessment-questions
              ref="assessmentQuestionsForm"
              :editable="editMode"
            />
          </template>
        </dx-edit-data-toggle>

        <!-- DATA PROTECTION ------------------------------------------------------------>
        <div class="row pt-5">
          <div class="col-12">
            <h3 class="mb-5">
              {{ $t('dataProtection') }}
            </h3>
            <p>
              {{ $t('offer.dataProtection.part1a') }}
              <a
                class="a-underline"
                target="_blank"
                :href="`${websiteURL}${$t('dataProtectionUrl')}`"
              >
                {{ $t('offer.dataProtection.link_avb_text') }}
              </a>
              {{ $t('offer.dataProtection.part1b') }}
            </p>
            <p>{{ $t('offer.dataProtection.part2') }}</p>
            <ul>
              <li>{{ $t('offer.dataProtection.part3') }}</li>
              <li>{{ $t('offer.dataProtection.part4') }}</li>
              <li>{{ $t('offer.dataProtection.part5') }}</li>
              <li>{{ $t('offer.dataProtection.part6') }}</li>
              <li>{{ $t('offer.dataProtection.part7') }}</li>
              <li>{{ $t('offer.dataProtection.part8') }}</li>
              <li>{{ $t('offer.dataProtection.part9') }}</li>
            </ul>

            <dx-radio-check
              id="agreeDataProtection"
              v-model="agreeDataProtection"
              :checked="agreeDataProtection"
              no-background
              :label="
                $t('offer.summary.agreeDataProtection', {
                  link_avb: `<a class='a-underline' target='_blank' rel='noopener'
                         href='${getAvbFile}'>${$t(
                    'offer.summary.link_avb',
                  )}</a>`,
                })
              "
              :error="errors.first('agreeDataProtection')"
              type="checkbox"
              name="agreeDataProtection"
              class="mt-5"
              data-vv-name="agreeDataProtection"
              @input="agreeDataProtection = !agreeDataProtection"
            />
          </div>
        </div>

        <div
          v-if="premia.totalPremium.value"
          class="col-12 col-md-4 mt-5 p-3 d-block d-md-none dotted-border"
        >
          <div class="text-center">
            CHF
            <span class="big-price">
              {{ formatCurrency(premia.totalPremium.value) }}
            </span>
            <span class="d-block">
              {{ $t('offer.tax') }}
            </span>
          </div>
        </div>

        <div class="row mt-5">
          <div id="submit-wrap" class="col-12 text-right">
            <!-- SUBMIT ------------------------------------------------------------>
            <dx-button
              :text="$t('offer.summary.close')"
              button-class="cmp-btn--forward"
              class="d-md-inline-block"
              @click="closeContract"
            />
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4 d-none d-md-block">
        <dx-sticky-price-container
          :selected-tariff="activeTariff"
          :price="premia.totalPremium.value"
        />
      </div>
    </div>
    <dx-loading-overlay v-if="loading" global-loader />
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import Cookies from 'js-cookie'
import {
  DxButton,
  DxRadioCheck,
} from '@sumcumo/dextra-frontend-component-lib-old'
import DxProgressbar from '~/components/DX-Progressbar'
import DxDownloadList from '~/components/DX-DownloadList'
import DxEditDataToggle from '~/components/DX-EditDataToggle'
import DxLoadingOverlay from '~/components/DX-LoadingOverlay'
import DxPrivateBasicQuestions from '~/components/offer/DX-PrivateBasicQuestions'
import DxBusinessBasicQuestions from '~/components/offer/DX-BusinessBasicQuestions'
import DxBusinessMoveBasicQuestions from '~/components/offer/DX-BusinessMoveBasicQuestions'
import DxPolicyHolderDataForm from '~/components/offer/DX-PolicyHolderData'
import DxAssessmentQuestions from '~/components/offer/DX-AssessmentQuestions'
import DxStickyPriceContainer from '~/domains/partnerPortal/components/DX-StickyPriceContainer'
import DxBusinessPolicyHolderDataForm from '~/components/offer/DX-BusinessPolicyHolderData'
import scrollFirstErrorIntoView, {
  scrollIntoView,
} from '~/javascripts/helper/scollIntoView'
import validationErrorsMixin from '~/javascripts/mixins/validationErrorsMixin'
import { formatCurrency } from '~/javascripts/utils'
import { trackConversion } from '~/javascripts/helper/webtrekk'

export default {
  name: 'OfferSummary',
  components: {
    DxProgressbar,
    DxDownloadList,
    DxEditDataToggle,
    DxRadioCheck,
    DxButton,
    DxStickyPriceContainer,
    DxPrivateBasicQuestions,
    DxBusinessBasicQuestions,
    DxBusinessMoveBasicQuestions,
    DxPolicyHolderDataForm,
    DxBusinessPolicyHolderDataForm,
    DxAssessmentQuestions,
    DxLoadingOverlay,
  },
  mixins: [validationErrorsMixin],
  layout: 'website',
  // maybe use a dynamic layout here some day?
  // layout: ({ store }) => (store.getters.isLoggedIn ? 'default' : 'website'),
  data() {
    return {
      agreeDataProtection: false,
      activeTariff: this.$store.state.offer.activeTariff,
      radioOptions: [
        {
          text: this.$t('yes'),
          id: true,
        },
        {
          text: this.$t('no'),
          id: false,
        },
      ],
      validForms: {
        basicQuestions: true,
        policyHolderData: true,
        answeredQuestions: true,
      },
      websiteURL: process.env.DEXTRA_WEBSITE_URL,
      tariffURL: `${process.env.DEXTRA_WEBSITE_URL}${this.$t(
        'navLinksTariffs.privatLegal',
      )}`,
    }
  },
  computed: {
    premia() {
      return this.$store.state.offer.premia
    },
    tariffType() {
      return this.$store.getters.GET_ACTIVE_TARIFF_TYPE
    },
    formRef() {
      let ref = 'smallBusinessIntroForm'
      if (this.tariffType === 'private') {
        ref = 'privateIntroForm'
      } else if (this.tariffType === 'business') {
        ref = 'businessIntroForm'
      } else {
        ref = 'businessMoveIntroForm'
      }
      return ref
    },
    formRefPolicyHolder() {
      return this.tariffType === 'private'
        ? 'policyHolderDataForm'
        : 'businessPolicyHolderDataForm'
    },
    getTariff() {
      switch (this.activeTariff[0]) {
        case 'p':
          return 'private'
        case 'b':
          return 'business'
        default:
          return 'smallBusiness'
      }
    },
    downloads() {
      const categories = Object.keys(this.$t('offer.downloads'))
      const downloads = []
      categories.forEach((category) => {
        const path = this.$t(
          `offer.downloads.${category}.${this.getTariff}.downloadFolder`,
        )
        const fileUrl = `${process.env.DEXTRA_WEBSITE_URL}/${path}/${this.$t(
          `offer.downloads.${category}.${this.getTariff}.file`,
        )}`
        downloads.push({
          filename: this.$t(
            `offer.downloads.${category}.${this.getTariff}.filename`,
          ),
          url: fileUrl,
        })
      })
      return downloads
    },
    getAvbFile() {
      const path = this.$t(
        `offer.downloads.avbData.${this.getTariff}.downloadFolder`,
      )
      return `${process.env.DEXTRA_WEBSITE_URL}/${path}/${this.$t(
        `offer.downloads.avbData.${this.getTariff}.file`,
      )}`
    },
    loading() {
      return this.$store.state.application.loading
    },
  },
  mounted() {
    this.calcPremia()
  },
  methods: {
    formatCurrency,
    brokerInfo() {
      const brokerCookie = Cookies.getJSON('broker') || {}
      const broker = {
        brokerId: brokerCookie.broker_id
          ? parseInt(brokerCookie.broker_id, 10)
          : null,
        origin: brokerCookie.origin
          ? brokerCookie.origin.replace(/\..*$/, '')
          : null,
      }
      return broker
    },
    async saveBasicQuestionsForm() {
      const valid = await this.$refs[this.formRef].validateForm()
      if (valid) {
        const result = await this.calcPremia()
        if (result) {
          this.$refs[this.formRef].saveForm()
          return result
        }
      }
      return false
    },
    resetBasicQuestionsForm() {
      this.$refs[this.formRef].resetForm()
      return true
    },
    async calcPremia() {
      this.$refs[this.formRef].$validator.reset()
      const actionKey = this.activeTariff.split('-').join('').toUpperCase()
      const action = `CALC_${actionKey}_PREMIA`
      const payload = this.$refs[this.formRef].formData
      const result = await this.$store.dispatch(action, payload)
      if (result) {
        const errors = result.validationErrors
        if (errors.length) {
          errors.forEach((err) => {
            this.$refs[this.formRef].$validator.errors.add(
              this.getServerValidationError(err),
            )
          })
          return false
        }
        return true
      }
      // OR NETWORK ERROR:
      this.$notification.error({ messageKey: 'notifications.error.message' })
      return false
    },
    debouncedCalcPremia: debounce(async function callCalcPremia() {
      const valid = await this.$refs[this.formRef].validateForm()
      if (valid) {
        this.calcPremia()
      }
    }, 1000),
    async saveAllForms() {
      const promises = []

      this.validForms.basicQuestions = true
      this.validForms.policyHolderData = true
      this.validForms.answeredQuestions = true

      if (this.$refs.basicQuestionsToogle.editMode) {
        promises.push(
          this.saveBasicQuestionsForm().then((result) => {
            this.validForms.basicQuestions = result
            return result
          }),
        )
      }
      if (this.$refs.policyHolderDataToogle.editMode) {
        promises.push(
          this.$refs[this.formRefPolicyHolder].saveForm().then((result) => {
            this.validForms.policyHolderData = result
            return result
          }),
        )
      }
      if (this.$refs.answeredQuestionsToogle.editMode) {
        promises.push(
          this.$refs.assessmentQuestionsForm.saveForm().then((result) => {
            this.validForms.answeredQuestions = result
            return result
          }),
        )
      }

      return Promise.all(promises).then((values) =>
        values.every((value) => value),
      )
    },
    async closeContract() {
      this.errors.clear()
      const hasNoClientErrors = await this.saveAllForms()
      if (!this.agreeDataProtection) {
        this.$validator.errors.add({
          field: 'agreeDataProtection',
          msg: '',
          rule: 'required',
        })
      } else if (hasNoClientErrors) {
        const actionKey = this.activeTariff.split('-').join('').toUpperCase()
        const action = `CREATE_${actionKey}_POLICY`
        const result = await this.$store.dispatch(action, this.brokerInfo())
        if (result) {
          const errors = result.validationErrors
          let scrolled = false

          if (
            errors.find(
              (error) => error.field.split('.')[0] === 'questionnaire',
            )
          ) {
            this.validForms.basicQuestions = false
            this.$refs.basicQuestionsToogle.editMode = true
            if (!scrolled) {
              scrollIntoView(this.$refs.basicQuestionsToogle.$el)
              scrolled = true
            }
          }
          if (
            errors.find((error) => error.field.split('.')[0] === 'policyholder')
          ) {
            this.validForms.policyHolderData = false
            this.$refs.policyHolderDataToogle.editMode = true
            if (!scrolled) {
              scrollIntoView(this.$refs.policyHolderDataToogle.$el)
              scrolled = true
            }
          } else if (
            errors.find(
              (error) => error.field.split('.')[0] === 'riskQuestions',
            )
          ) {
            this.validForms.answeredQuestions = false
            this.$refs.answeredQuestionsToogle.editMode = true
            if (!scrolled) {
              scrollIntoView(this.$refs.answeredQuestionsToogle.$el)
              scrolled = true
            }
          }

          const errorKeys = []
          errors.forEach((err) => {
            if (!errorKeys.includes(err.key)) {
              this.$notification.error({
                messageKey: this.getValidationErrorMessageKey(err),
              })
              errorKeys.push(err.key)
            }
          })
          if (result.success) {
            trackConversion({
              price: this.premia.totalPremium.value,
              locale: this.$i18n.locale,
              tariff: this.activeTariff,
            })
            this.$store.commit('SET_STEP', 5)
            this.$router.push({
              path: this.localePath('Offer-ThankYou'),
              query: { underwriting: `${result.underwriting}` },
            })
          }
        } else {
          // OR NETWORK ERROR:
          this.$notification.error({
            messageKey: 'notifications.error.message',
          })
        }
      } else {
        scrollFirstErrorIntoView()
      }
    },
  },
  meta: {
    isPublic: true,
    trackingId: 'dextra.rechtsschutz-antragsstrecke.zusammenfassung',
    offerStep: 4,
  },
  nuxtI18n: {
    paths: {
      de: '/rechtsschutz-antragsstrecke/zusammenfassung/',
      fr: '/protection-juridique-formulaire-de-demande/resume/',
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'index';
</style>
